import { type GenericMap, type UnitStandardKey } from "@common/declarations";
import { isUndefined } from "lodash";
import { v4 as uuid, validate as validateUuid } from "uuid";
import Deferred from "./deferred";

export const EMPTY_OBJECT = {};
export const MAX_DB_INT = 2000000000;

export const afterSuccess =
  (fn: (...args: any) => Promise<any> | void, onSuccess: () => void) =>
  async (...args: any) => {
    await fn(...args);
    onSuccess();
  };

export const identityPromise = (): Promise<void> => Promise.resolve();

export const evalInContext = <T = any>(context: object, js: string): T | undefined => {
  // tslint:disable-next-line:only-arrow-functions
  return function () {
    try {
      // tslint:disable-next-line:no-eval
      return eval(js) as T;
    } catch (e) {
      console.error(e, js);
    }
  }.call(context);
};

export const isDev = () => process.env.NODE_ENV === "development";

export const isBrowser = () => typeof window !== "undefined" && !window["isSsr"];

export const isVisible = (visibilityCondition: string | null | undefined, values: GenericMap<any>): boolean => {
  if (visibilityCondition) {
    const isVisible = evalInContext({ f: values }, visibilityCondition.replace(/f\./g, "this.f."));
    if (!isUndefined(isVisible) && !isVisible) return false;
  }
  return true;
};

export const labelize = (s: string | undefined | null): string => {
  if (!s) return "";

  return s
    .replace(/([^\s]{1})([A-Z]{1}[a-z]{1})/g, (_, a, b) => `${a} ${b}`)
    .replace(/([a-z]{1})([A-Z]{1})/g, (_, a, b) => `${a} ${b}`)
    .trim()
    .replace(/^([a-z]{1})/g, (a) => `${a}`.toUpperCase())
    .replace(/([a-zA-Z]+)(\d+)([a-zA-Z]*)/, (_, a, b, c) => `${a} ${b}${c ? ` ${c}` : ""}`);
};

const NUMBER_GROUPS = /(-?\d*\.?\d+)/g;
export const naturalCompare = (a: any, b: any): number => {
  const aa = String(a).split(NUMBER_GROUPS),
    bb = String(b).split(NUMBER_GROUPS),
    min = Math.min(aa.length, bb.length);

  for (var i = 0; i < min; i++) {
    const x = aa[i].toLowerCase(),
      y = bb[i].toLowerCase();
    if (x < y) return -1;
    if (x > y) return 1;
    i++;
    if (i >= min) break;
    const z = parseFloat(aa[i]) - parseFloat(bb[i]);
    if (z != 0) return z;
  }
  return aa.length - bb.length;
};

const getScrollParent = (element: HTMLElement, includeHidden: boolean): HTMLElement | null => {
  let style = getComputedStyle(element);
  const excludeStaticParent = style.position === "absolute";
  const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

  if (style.position === "fixed") return document.body;
  for (let parent: HTMLElement | null = element; (parent = parent.parentElement); ) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === "static") {
      continue;
    }
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) return parent;
  }

  return document.body;
};

export const scrollParentToChild = (child: HTMLElement, position: "center") => {
  const parent = getScrollParent(child, true);
  if (!parent) return;

  // Where is the parent on page
  const parentRect = parent.getBoundingClientRect();
  // What can you see?
  const parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth
  };

  // Where is the child
  const childRect = child.getBoundingClientRect();
  // Is the child viewable?
  const isViewable = childRect.top >= parentRect.top && childRect.bottom <= parentRect.top + parentViewableArea.height;

  // if you can't see the child try to scroll parent
  if (!isViewable) {
    // Should we scroll using top or bottom? Find the smaller ABS adjustment
    const scrollTop = childRect.top - parentRect.top - (parentRect.height / 2 - childRect.height / 2);
    const scrollBot = childRect.bottom - parentRect.bottom + (parentRect.height / 2 - childRect.height / 2);
    if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
      // we're near the top of the list
      parent.scrollTop += scrollTop;
    } else {
      // we're near the bottom of the list
      parent.scrollTop += scrollBot;
    }
  }
};

export const scrollToElement = (el: HTMLElement) => {
  const top = el.getBoundingClientRect().top;

  scrollTo({
    behavior: "smooth",
    top: top
  });
};

export const toCamelCase = (name: string): string =>
  name.replace(/^([A-Z]+)/, (_, a) =>
    a.length === 1 ? a.toLowerCase() : a.substr(0, a.length - 1).toLowerCase() + a[a.length - 1]
  );

export const pascalCase = (name: string): string => name.charAt(0).toUpperCase() + name.slice(1);

export const traverse = (obj: object, enter: (obj: object, key: string) => void) => {
  Object.keys(obj).forEach((k) => {
    if (obj[k] && typeof obj[k] === "object") {
      traverse(obj[k], enter);
    } else {
      enter(obj, k);
    }
  });
};

export { uuid };
export const isUuid = (v: string): boolean => validateUuid(v);

export const joinDefined = (separator: string, ...args: Array<string | null | undefined>) =>
  args.filter((x) => !!x).join(separator);

export const scrollToEl = (selector: string, parent: HTMLElement) => {
  const child = document.querySelector(selector) as HTMLElement;
  if (!child) return;

  parent.scrollTop = child.offsetTop;
};

export const isNullOrUndefined = <T>(obj: T | null | undefined): obj is null | undefined => {
  return typeof obj === "undefined" || obj === null;
};

export const waitForElement = (selector: string, timeout = 20000, interval = 30): Deferred<HTMLElement | undefined> => {
  const deferred = new Deferred<HTMLElement | undefined>();

  const startTime = Date.now();
  const checkExist = () => {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect.top > 0) {
        deferred.resolve(element);
      } else {
        setTimeout(checkExist, interval);
      }
    } else if (Date.now() - startTime >= timeout) {
      deferred.reject();
    } else {
      setTimeout(checkExist, interval);
    }
  };

  checkExist();

  return deferred;
};

export const getLengthUnitLabel = (unit: UnitStandardKey): string => {
  switch (unit) {
    case "Imperial":
      return "in";
    case "Metric":
      return "mm";
  }
};

export const getWeightUnitLabel = (unit: UnitStandardKey): string => {
  switch (unit) {
    case "Imperial":
      return "oz";
    case "Metric":
      return "g";
  }
};
